import React, {Component} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import InspectionViewMap from "../inspections/InspectionViewMap";
import ReportInspectionDiagram from "./ReportInspectionDiagram";
import ReportStructuresStats from "./ReportStructuresStats";

const withNavigate = Component => props => {
  const navigate = useNavigate();
  return <Component {...props} navigate={navigate} />;
};

const withParams = Component => props => {
  const params = useParams();
  return <Component {...props} params={params} />;
};


class ReportViewPage extends Component {
    constructor(props) {
        super(props);

        this.scrollableNodeRef = React.createRef();

        this.state = {
            id: props.params.id,
            data: {},
            sorting: 'name',
            structures: [],
            structuresExpanded: {},
            shownAnnotation: null,
            priority: [],
            searchTerm: '',
            mapVisible: props.mapVisible,
            moreInfoVisible: false,
            activeStructureIdentifier: null
        };

        window.appComponent.setState({
            loaderVisible: true
        });

        this.onRowClicked = this.onRowClicked.bind(this);
        this.sortStructures = this.sortStructures.bind(this);
        this.onStructureRowClick = this.onStructureRowClick.bind(this);
        this.onStructureSelected = this.onStructureSelected.bind(this);
        this.onSortingChange = this.onSortingChange.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onBackClick = this.onBackClick.bind(this);
        this.onMapTogglerClick = this.onMapTogglerClick.bind(this);
        this.onAnnotationsCollapse = this.onAnnotationsCollapse.bind(this);
        this.updateRemoteDate = this.updateRemoteDate.bind(this);
        this.onIssueClicked = this.onIssueClicked.bind(this);
    }


    severityToText(severity) {
        switch (severity) {
            case 1:
                return 'good';
            case 2:
                return 'low';
            case 3:
                return 'medium';
            case 4:
                return 'high';
            default:
                return 'good';
        }
    }


    updateRemoteDate() {
        const promiseInfo = window.apiHelper.getWorkorderInfo(this.state.id),
            promiseStructures = window.apiHelper.getWorkorderStructures(this.state.id);

        Promise.all([promiseInfo, promiseStructures]).then((values) => {
            const data = values[0],
                structures = values[1];
            document.title = 'Report for '+data.linenumber + ' — ' + process.env.REACT_APP_PAGE_TITLE;

            //console.log(data);

            this.setState({
                data: data,
                structures: structures,
            }, () => {
                window.appComponent.setState({
                    loaderVisible: false
                });
            });
        });
    }


    componentDidMount() {
        this.updateRemoteDate();
    }


    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevState.id !== this.state.id) {
            console.log('id changed');
            this.updateRemoteDate();
        }

        if (prevState.priority !== this.state.priority) {
            console.log('priority changed');
        }
    }


    onStructureRowClick(e) {
        e.preventDefault();

        e.target.closest('.report-structure-row').classList.toggle('active');
    }


    onStructureSelected(structureIdentifier) {
        if (!structureIdentifier) {structureIdentifier = null;}

        if (this.state.activeStructureIdentifier !== structureIdentifier) {
            this.setState({
                activeStructureIdentifier: structureIdentifier
            }, () => {
                if (structureIdentifier) {
                    let target = document.querySelector('.structure-issues[data-title="'+structureIdentifier+'"]');
                    if (target) {
                        this.scrollableNodeRef.current.scrollTop = target.offsetTop;

                        const detectedIssuesTab = target.querySelector('.issues-header .detected-issues');
                        if (!detectedIssuesTab.classList.contains('active')) {
                            detectedIssuesTab.click();
                        }
                    }
                }
            });
        }
    }


    onBackClick(e) {
        this.props.navigate('/reports');
    }


    onSearch(e) {
        e.preventDefault();

        const searchTerm = document.querySelector('input#annotation-search').value;

        this.setState({
            searchTerm: searchTerm
        });
    }


    onSortingChange(e, type) {
        e.preventDefault();

        this.setState({
            sorting: type
        });
    }


    onMapTogglerClick(e) {
        this.setState({
            mobileMapVisible: !this.state.mobileMapVisible
        });
    }


    onAnnotationsCollapse(e, structureIdentifier, type) {
        e.preventDefault();
        e.stopPropagation();

        let structuresExpanded = this.state.structuresExpanded;
        if (structuresExpanded.hasOwnProperty(structureIdentifier) && structuresExpanded[structureIdentifier] === type) {
            structuresExpanded[structureIdentifier] = null;
        } else {
            structuresExpanded[structureIdentifier] = type;
        }

        this.setState({
            structuresExpanded: structuresExpanded
        });
    }


    onRowClicked(identifier) {
        this.setState({
            activeStructureIdentifier: identifier
        });
    }


    formatDateTime(datetime, shortForm) {
        const dateRaw = Date.parse(datetime);
        let formattedDate = '-';
        if (!isNaN(dateRaw)) {
            const date = new Date(dateRaw);

            const months = shortForm ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

            const dateFormatted = date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear();
            const timeFormatted = date.getHours() + ':' + date.getMinutes().toString().padStart(2, '0');

            formattedDate = dateFormatted + ', ' + timeFormatted;
        }

        return formattedDate;
    }


    countPreparedPhotos() {
        let count = 0;

        this.state.data && this.state.data.objects &&
        this.state.data.objects.forEach((object) => {
            count += object.photos.length;
        });

        return count;
    }


    isSearched(annotation, structureIdentifier) {
        if (!this.state.searchTerm || !annotation) {return true;}
        const searchTerm = this.state.searchTerm.toLowerCase();

        let isSearched = false;
        if (
            structureIdentifier && structureIdentifier.toString().toLowerCase().indexOf(searchTerm) >= 0 ||
            annotation.feature && annotation.feature.toString().toLowerCase().indexOf(searchTerm) >= 0 ||
            annotation.defect && annotation.defect.join(',').toLowerCase().indexOf(searchTerm) >= 0 ||
            annotation.description && annotation.description.toString().toLowerCase().indexOf(searchTerm) >= 0
        ) {isSearched = true;}

        return isSearched;
    }


    getObjectLatLngById(id) {
        let coords = {};
        for (let i=0; i<this.state.data.objects.length; i++) {
            if (id == this.state.data.objects[i].title) {
                coords = {
                    lat: this.state.data.objects[i].lat,
                    lng: this.state.data.objects[i].lng
                };
            }
        }

        return coords;
    }


    onIssueClicked(annotation, structureId) {
        this.showAnnotation(annotation, structureId);
    }


    getStructuresPriorities() {
        let priorities = {};

        if (this.state.structures) {
            this.state.structures.forEach((structure) => {
                if (structure.annotations) {
                    structure.annotations.forEach((annotation) => {
                        if (!priorities[structure.identifier]) {
                            priorities[structure.identifier] = {
                                high: false,
                                medium: false,
                                low: false
                            };
                        }

                        priorities[structure.identifier][this.getPriorityLabel(annotation.severity)] = true;
                    });
                }
            });
        }

        return priorities;
    }


    getTopAnnotations() {
        let annotations = {};

        if (this.state.structures) {
            this.state.structures.forEach((structure) => {
                if (structure.annotations) {
                    structure.annotations.forEach((annotation) => {
                        if (annotation.defect) {
                            annotation.defect.forEach((defect) => {
                                const defectKey = annotation.feature + ': ' + defect;
                                if (annotations[defectKey] === undefined) {annotations[defectKey] = 0;} else {annotations[defectKey]++;}
                            });
                        }
                    });
                }
            });
        }

        //select top 3 keys by values
        let topAnnotations = {};
        for (let i = 0; i < 3; i++) {
            let topKey = null,
                topValue = 0;
            for (let key in annotations) {
                if (annotations[key] > topValue) {
                    topKey = key;
                    topValue = annotations[key];
                }
            }
            if (topKey) {
                topAnnotations[topKey] = topValue;
                delete annotations[topKey];
            }
        }

        return topAnnotations;
    }


    getPriorityLabel(index) {
        switch (index) {
            case 4: return 'high';
            case 3: return 'medium';
            case 2: return 'low';
            default: return 'good';
        }
    }


    sortStructures(structures) {
        if (!structures) {return [];}

        let sortedStructures = [...structures];
        switch (this.state.sorting) {
            /*case 'name':
                sortedStructures = structures.sort((a, b) => {
                    if (a.identifier < b.identifier) {
                        return -1;
                    }
                    if (a.identifier > b.identifier) {
                        return 1;
                    }
                    return 0;
                });
                break;*/
            case 'importance':
                sortedStructures = sortedStructures.sort((a, b) => {
                        const aPriority = this.getStructureImportance(a),
                            bPriority = this.getStructureImportance(b);
                        if (aPriority > bPriority) {
                            return -1;
                        }
                        if (aPriority < bPriority) {
                            return 1;
                        }
                        return 0;
                    }
                );
                break;
        }

        return sortedStructures;
    }


    getStructureImportance(structure) {
        let importance = 0;
        if (structure.annotations) {
            structure.annotations.forEach((annotation) => {
                importance += annotation.severity;
            });
        }

        return importance;
    }


    render() {
        let scrollHeight = window.innerHeight - 25, elHeight;
        if (document.querySelector('.panel-top')) {
            elHeight = document.querySelector('.panel-top').offsetHeight;
            if (typeof elHeight !== 'undefined') {
                scrollHeight -= elHeight;
            }
        }
        if (document.querySelector('.report-view')) {
            elHeight = document.querySelector('.report-view').offsetHeight;
            if (typeof elHeight !== 'undefined') {
                scrollHeight -= elHeight;
            }
        }
        if (document.querySelector('.report-data h5:first-child')) {
            elHeight = document.querySelector('.report-data h5:first-child').offsetHeight;
            if (typeof elHeight !== 'undefined') {
                scrollHeight -= elHeight;
            }
        }
        if (window.innerWidth <= 1199) {scrollHeight *= 10;}

        const isMobile = (window.innerWidth <= 1199);

        const inspection = this.state.data;
        if (!inspection) {return (<></>);}
        if (!inspection.linenumber) {inspection.linenumber = 'TMP DATA';}

        const structuresSorted = this.sortStructures(this.state.structures);

        return (
            <>
                <div id="main-panel">
                    {inspection.linenumber && <>
                    <div className="panel-top">
                        <div className="back">
                            <button onClick={this.onBackClick}>
                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M4.4802 0.787897C4.66043 0.607652 4.66043 0.315425 4.4802 0.13518C4.29997 -0.04506 4.00772 -0.04506 3.82749 0.13518L0.13518 3.82749C-0.04506 4.00772 -0.04506 4.29997 0.13518 4.4802L3.82749 8.17251C4.00772 8.35274 4.29997 8.35274 4.4802 8.17251C4.66043 7.99228 4.66043 7.70003 4.4802 7.5198L1.57579 4.61538H11.5385C11.7934 4.61538 12 4.40875 12 4.15385C12 3.89894 11.7934 3.69231 11.5385 3.69231H1.57579L4.4802 0.787897Z"
                                        fill="#151741"/>
                                </svg>
                            </button>
                        </div>

                        <div className="report-title">
                            <h1>Report for {inspection.linenumber} #{inspection.id}</h1>
                            <span>{this.formatDateTime(inspection.created_at)}</span>
                        </div>
                        <div className="btn-group">
                            <button>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.78587 3.23187C7.32313 2.11236 8.48347 1.33333 9.83333 1.33333C11.7023 1.33333 13.2 2.82284 13.2 4.63887C13.2 4.77273 13.1919 4.9044 13.1763 5.03353C13.1383 5.34813 13.3269 5.64607 13.6276 5.74613C14.2381 5.94933 14.6667 6.51387 14.6667 7.16667C14.6667 7.98553 13.9898 8.66667 13.1333 8.66667H11.6667C11.2985 8.66667 11 8.96513 11 9.33333C11 9.70153 11.2985 10 11.6667 10H13.1333C14.7069 10 16 8.741 16 7.16667C16 6.0996 15.4048 5.17687 14.533 4.69373C14.5332 4.67547 14.5333 4.6572 14.5333 4.63887C14.5333 2.06734 12.4195 0 9.83333 0C8.15007 0 6.67027 0.87418 5.83987 2.19174C5.70653 2.17518 5.5708 2.16667 5.43333 2.16667C4.0004 2.16667 2.77695 3.08986 2.35599 4.37827C1.02275 4.6158 0 5.76653 0 7.16667C0 8.741 1.29308 10 2.86667 10H4.33333C4.70153 10 5 9.70153 5 9.33333C5 8.96513 4.70153 8.66667 4.33333 8.66667H2.86667C2.0102 8.66667 1.33333 7.98553 1.33333 7.16667C1.33333 6.3478 2.0102 5.66667 2.86667 5.66667H2.89266C3.22383 5.66667 3.50481 5.42353 3.55241 5.0958C3.68231 4.2014 4.46853 3.5 5.43333 3.5C5.62907 3.5 5.81667 3.52879 5.9926 3.58176C6.30713 3.67646 6.64373 3.52798 6.78587 3.23187Z" fill="#CACACA"/>
                                    <path d="M8.00001 7.3C8.36821 7.3 8.66668 7.59847 8.66668 7.96667V13.6905L9.72861 12.6286C9.98895 12.3683 10.4111 12.3683 10.6714 12.6286C10.9317 12.8889 10.9317 13.3111 10.6714 13.5714L8.47141 15.7714C8.21108 16.0317 7.78895 16.0317 7.52861 15.7714L5.32861 13.5714C5.06828 13.3111 5.06828 12.8889 5.32861 12.6286C5.58895 12.3683 6.01108 12.3683 6.27141 12.6286L7.33335 13.6905V7.96667C7.33335 7.59847 7.63181 7.3 8.00001 7.3Z" fill="#CACACA"/>
                                </svg>
                            </button>
                            <button className="d-none">
                                <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.48354 4.92308C5.78422 4.92308 4.40662 6.30068 4.40662 8C4.40662 9.69933 5.78422 11.0769 7.48354 11.0769C9.18286 11.0769 10.5605 9.69933 10.5605 8C10.5605 6.30068 9.18286 4.92308 7.48354 4.92308ZM5.63739 8C5.63739 6.98037 6.46397 6.15385 7.48354 6.15385C8.50317 6.15385 9.32969 6.98037 9.32969 8C9.32969 9.01963 8.50317 9.84616 7.48354 9.84616C6.46397 9.84616 5.63739 9.01963 5.63739 8Z" fill="#CACACA"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M6.86815 0C5.84851 0 5.02199 0.826554 5.02199 1.84615V2.74942C5.02199 2.89715 4.92445 3.08089 4.71153 3.20422C4.50211 3.32548 4.29811 3.31855 4.16759 3.24318L3.38493 2.79132C2.50192 2.28151 1.37283 2.58406 0.863031 3.46706L0.247647 4.53292C-0.262156 5.41594 0.040385 6.54505 0.923388 7.05483L1.70607 7.50671C1.83586 7.58166 1.94506 7.75668 1.94506 8C1.94506 8.24332 1.83586 8.41834 1.70608 8.49329L0.923382 8.94517C0.040385 9.45495 -0.262156 10.5841 0.247647 11.4671L0.863031 12.5329C1.37283 13.4159 2.50192 13.7185 3.38492 13.2087L4.16759 12.7568C4.29811 12.6815 4.50211 12.6745 4.71153 12.7958C4.92445 12.9191 5.02199 13.1028 5.02199 13.2506V14.1538C5.02199 15.1735 5.84851 16 6.86815 16H8.09892C9.11848 16 9.94507 15.1735 9.94507 14.1538V13.2506C9.94507 13.1028 10.0426 12.9191 10.2555 12.7958C10.4649 12.6745 10.6689 12.6814 10.7995 12.7568L11.5822 13.2087C12.4651 13.7185 13.5942 13.4159 14.1041 12.5329L14.7195 11.4671C15.2293 10.5841 14.9267 9.45495 14.0437 8.94517L13.2609 8.49323C13.1312 8.41834 13.022 8.24332 13.022 8C13.022 7.75668 13.1312 7.58166 13.2609 7.50677L14.0437 7.05483C14.9267 6.54505 15.2293 5.41594 14.7195 4.53292L14.1041 3.46706C13.5942 2.58406 12.4652 2.28151 11.5822 2.79132L10.7995 3.24319C10.6689 3.31855 10.4649 3.32549 10.2555 3.20422C10.0426 3.0809 9.94507 2.89714 9.94507 2.7494V1.84615C9.94507 0.826554 9.11848 0 8.09892 0H6.86815ZM6.25276 1.84615C6.25276 1.50629 6.52827 1.23077 6.86815 1.23077H8.09892C8.43879 1.23077 8.7143 1.50629 8.7143 1.84615V2.7494C8.7143 3.43018 9.13473 3.97739 9.63873 4.26928C10.1469 4.56357 10.8273 4.64831 11.4149 4.30905L12.1976 3.85719C12.4919 3.68726 12.8683 3.78811 13.0382 4.08244L13.6536 5.14831C13.8235 5.44265 13.7227 5.81902 13.4283 5.98892L12.6456 6.44086C12.0575 6.78037 11.7912 7.4144 11.7912 8C11.7912 8.58554 12.0575 9.21963 12.6456 9.55914L13.4283 10.011C13.7227 10.181 13.8235 10.5574 13.6536 10.8517L13.0382 11.9175C12.8683 12.2119 12.4919 12.3127 12.1976 12.1428L11.4149 11.691C10.8273 11.3517 10.1469 11.4364 9.63873 11.7307C9.13473 12.0226 8.7143 12.5698 8.7143 13.2506V14.1538C8.7143 14.4937 8.43879 14.7692 8.09892 14.7692H6.86815C6.52827 14.7692 6.25276 14.4937 6.25276 14.1538V13.2506C6.25276 12.5698 5.83233 12.0226 5.32833 11.7307C4.82015 11.4364 4.13978 11.3517 3.55218 11.691L2.76954 12.1428C2.4752 12.3127 2.09884 12.2119 1.92891 11.9175L1.31352 10.8517C1.14359 10.5574 1.24443 10.181 1.53877 10.011L2.32146 9.55914C2.90954 9.21963 3.17583 8.5856 3.17583 8C3.17583 7.4144 2.90954 6.78037 2.32146 6.44086L1.53877 5.98892C1.24443 5.81902 1.14359 5.44265 1.31352 5.14831L1.92891 4.08244C2.09884 3.78811 2.4752 3.68726 2.76954 3.85719L3.55219 4.30905C4.13978 4.64831 4.82015 4.56357 5.32833 4.26927C5.83233 3.97739 6.25276 3.43018 6.25276 2.74942V1.84615Z" fill="#CACACA"/>
                                </svg>
                            </button>

                            {isMobile &&
                            <button onClick={this.onMapTogglerClick}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M5.53845 5.53846C5.53845 4.17899 6.64054 3.07692 7.99999 3.07692C9.35944 3.07692 10.4615 4.17899 10.4615 5.53846C10.4615 6.8979 9.35944 8 7.99999 8C6.64054 8 5.53845 6.8979 5.53845 5.53846ZM7.99999 4.30769C7.32024 4.30769 6.76922 4.8587 6.76922 5.53846C6.76922 6.21821 7.32024 6.76923 7.99999 6.76923C8.67974 6.76923 9.23076 6.21821 9.23076 5.53846C9.23076 4.8587 8.67974 4.30769 7.99999 4.30769Z"
                                          fill="#333333"/>
                                    <path fillRule="evenodd" clipRule="evenodd"
                                          d="M2.46155 5.53846C2.46155 2.47966 4.94118 0 8.00001 0C11.0588 0 13.5385 2.47966 13.5385 5.53846C13.5385 6.14855 13.2513 6.94972 12.8841 7.73249C12.5031 8.54455 11.9869 9.4368 11.4351 10.2644C10.8843 11.0906 10.2859 11.8708 9.73521 12.4521C9.46099 12.7415 9.18481 12.9964 8.92075 13.1836C8.6797 13.3544 8.35416 13.5385 8.00001 13.5385C7.64586 13.5385 7.32032 13.3544 7.07927 13.1836C6.81521 12.9964 6.53902 12.7415 6.26481 12.4521C5.7141 11.8708 5.1157 11.0906 4.56487 10.2644C4.01313 9.4368 3.49691 8.54455 3.11597 7.73249C2.74872 6.94972 2.46155 6.14855 2.46155 5.53846ZM8.00001 1.23077C5.62093 1.23077 3.69232 3.15939 3.69232 5.53846C3.69232 5.85145 3.86668 6.43489 4.23021 7.20978C4.58001 7.95545 5.06382 8.79397 5.58899 9.58172C6.11509 10.3709 6.67053 11.0907 7.15829 11.6056C7.40327 11.8642 7.61749 12.0565 7.79096 12.1794C7.90444 12.2599 7.97207 12.2902 8.00001 12.3014C8.02795 12.2902 8.09558 12.2599 8.20906 12.1794C8.38253 12.0565 8.59675 11.8642 8.84173 11.6056C9.32949 11.0907 9.88493 10.3709 10.411 9.58172C10.9362 8.79397 11.42 7.95545 11.7698 7.20978C12.1334 6.43489 12.3077 5.85145 12.3077 5.53846C12.3077 3.15939 10.3791 1.23077 8.00001 1.23077Z"
                                          fill="#333333"/>
                                    <path
                                        d="M3.25286 11.678C3.57854 11.5809 3.76379 11.2381 3.66662 10.9124C3.56946 10.5867 3.22667 10.4015 2.90099 10.4986C2.11025 10.7345 1.42165 11.0359 0.916892 11.4042C0.424517 11.7634 0 12.27 0 12.9231C0 13.5323 0.370978 14.0148 0.8148 14.3646C1.26556 14.7198 1.87967 15.0128 2.58369 15.2474C3.99815 15.719 5.91305 16 8 16C10.087 16 12.0018 15.719 13.4163 15.2474C14.1203 15.0128 14.7345 14.7198 15.1852 14.3646C15.629 14.0148 16 13.5323 16 12.9231C16 12.27 15.5755 11.7634 15.0831 11.4042C14.5783 11.0359 13.8897 10.7345 13.099 10.4986C12.7734 10.4015 12.4305 10.5867 12.3334 10.9124C12.2362 11.2381 12.4215 11.5809 12.7471 11.678C13.4672 11.8929 14.0094 12.1443 14.3577 12.3985C14.7183 12.6617 14.7692 12.8471 14.7692 12.9231C14.7692 12.9936 14.727 13.1586 14.4234 13.3979C14.1268 13.6316 13.6594 13.869 13.0271 14.0798C11.7689 14.4993 9.99145 14.7692 8 14.7692C6.00855 14.7692 4.23114 14.4993 2.97289 14.0798C2.34055 13.869 1.87322 13.6316 1.57661 13.3979C1.27306 13.1586 1.23077 12.9936 1.23077 12.9231C1.23077 12.8471 1.28164 12.6617 1.64234 12.3985C1.99066 12.1443 2.53282 11.8929 3.25286 11.678Z"
                                        fill="#333333"/>
                                </svg>
                            </button>
                            }
                        </div>
                    </div>

                    <div className="inspection-view report-view">
                        {inspection.stats &&
                        <>
                            <ReportInspectionDiagram
                                structuresPriorities={this.getStructuresPriorities()}
                            />
                            <ReportStructuresStats
                                stats={inspection.stats}
                                topAnnotations={this.getTopAnnotations()}
                            />
                        </>
                        }
                    </div>

                    <div className="report-data">
                        <h5>Structures</h5>

                        <ul className="sorting-elements">
                            <li>Sort by</li>
                            <li className={this.state.sorting === 'name' ? 'active' : ''} onClick={(e) => this.onSortingChange(e, 'name')}>Name</li>
                            <li className={this.state.sorting === 'importance' ? 'active' : ''} onClick={(e) => this.onSortingChange(e, 'importance')}>Importance</li>
                        </ul>

                        <SimpleBar
                            scrollableNodeProps={{ ref: this.scrollableNodeRef }}
                            style={{ maxHeight: scrollHeight, overflowX: 'hidden', paddingRight: 13, marginRight: -13 }}
                            autoHide={true}
                        >
                            <div className="report-structures-container">
                                {structuresSorted &&
                                structuresSorted.map((structure, i, structures) => {
                                    if (structure.annotations && structure.annotations.length > 0) {
                                        let outputs = [];
                                        for (const annotation of structure.annotations) {
                                            let results = (<div
                                                key={annotation.id.toString()}
                                                className={this.getPriorityLabel(annotation.severity)}
                                            >{annotation.feature}: {annotation.defect.join(', ')}</div>);

                                            outputs.push(results);
                                        }

                                        return (<div key={structure.identifier.toString()} className="report-structure-row">
                                            <div className="row-title" onClick={this.onStructureRowClick}>
                                                <span>{structure.identifier}</span>
                                                <span>
                                                    <svg className="plus" width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 0.5L5.5 11.5M0 6H11" stroke="black"/></svg>
                                                    <svg className="minus" width="11" height="2" viewBox="0 0 11 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 0.265625V0.5M0 0.734406H11" stroke="black"/></svg>
                                                </span>
                                            </div>

                                            <div className="annotations">
                                                {outputs}
                                            </div>
                                        </div>);
                                    }
                                })}
                            </div>
                        </SimpleBar>
                    </div>
                    </>}
                </div>

                <InspectionViewMap
                    structures={this.state.structures}
                    priority={this.state.priority}
                    searchTerm={this.state.searchTerm}
                    activeStructureIdentifier={this.state.activeStructureIdentifier}

                    mobileMapVisible={this.state.mobileMapVisible}

                    onStructureSelected={this.onStructureSelected}
                />
            </>
        );
    }
}

export default withParams(withNavigate(ReportViewPage));
